<script setup>
import {IonButton, IonButtons, IonHeader, IonIcon, IonModal, IonTitle, IonToolbar} from '@ionic/vue'
import {onMounted, onUnmounted, ref, watch} from 'vue'
import {useGlobalEmitter} from "@/helper/emitter.ts";

const props = defineProps({
    modelValue: Boolean,
    title: String,
    subtitle: {
        type: String,
        required: false,
        default: undefined,
    },
    fullscreen: Boolean,
    toolbar: {
        type: Boolean,
        default: (props) => props.fullscreen ? false : true,
        required: false,
    },
    transparentToolbar: {
        type: Boolean,
        default: false,
        required: false,
    },
    stacking: {
        type: Boolean,
        default: false,
        required: false,
    },
    keepFooterOnKeyboardShow: {
        type: Boolean,
        default: false,
        required: false
    },
    dataCy: {
        type: String,
        required: false,
    }
})

const emit = defineEmits(['update:modelValue', "view:willPresent", "view:didPresent", "view:willDismiss", "view:didDismiss"])



/**
 * ION Modal control
 */

const ionModalIsOpen = ref(props.modelValue) // inherit initial value from props

// sync external v-model to internal open state
watch(() => props.modelValue, value => {
    ionModalIsOpen.value = value
}, { immediate: true })

const ionModalRef = ref()
const ionModalWillPresent = () => emit("view:willPresent")
const ionModalDidPresent = () => emit("view:didPresent")
const ionModalWillDismiss = () => dismiss()
const ionModalDidDismiss = () => emit("view:didDismiss")
const dismiss = () => {
    emit("view:didDismiss")
    return new Promise(
        (resolve) => {
            if (ionModalRef.value) {
                ionModalRef.value.$el.onDidDismiss().then(resolve)
            }

            ionModalIsOpen.value = false
            emit('update:modelValue', false)
        }
    )
}
defineExpose({
    dismiss
})

/**
 * global close listener
 */

useGlobalEmitter().on('CloseAllModals', () => {
    if (ionModalIsOpen.value) {
        dismiss()
    }
})

/**
 * Keyboard & Stacking
 */

const keyboardEventsAreRegistered = ref(true)
const keyboardHeight = ref(0)
const presentingElement = ref(undefined)

const determinePresentingElement = () => {
    return
    if (!props.stacking) {
        return
    }

    const $appContainer = document.querySelector('ion-app')
    const $page = $appContainer && $appContainer.querySelector('div.ion-page')

    presentingElement.value = $page
}

const onKeyboardDidShow = ev => setKeyboardIsVisible(true, ev.detail.keyboardHeight)
const onKeyboardWillShow = () => { setTimeout(() => { setKeyboardIsVisible(true) }, 100) }
const onKeyboardDidHide = () => setKeyboardIsVisible(false)
const onKeyboardWillHide = () => { setTimeout(() => { setKeyboardIsVisible(false) }, 100) }

const setKeyboardIsVisible = (value, height) => {
    keyboardHeight.value = height || 0

    const $el = ionModalRef.value?.$el
    
    if (!$el) {
        return
    }

    if (value) {
        $el.classList.add('keyboard-visible')
    }
    else {
        $el.classList.remove('keyboard-visible')
    }
}

const registerKeyboardEvents = () => {
    if (keyboardEventsAreRegistered.value) {
        return
    }

    keyboardEventsAreRegistered.value = true
    window.addEventListener('ionKeyboardDidShow', onKeyboardDidShow)
    window.addEventListener('ionKeyboardDidHide', onKeyboardDidHide)
    window.addEventListener("keyboardWillShow", onKeyboardWillShow);
    window.addEventListener("keyboardWillHide", onKeyboardWillHide);
}

const unregisterKeyboardEvents = () => {
    onKeyboardDidHide()
    
    keyboardEventsAreRegistered.value = false
    window.removeEventListener('ionKeyboardDidShow', onKeyboardDidShow)
    window.removeEventListener('ionKeyboardDidHide', onKeyboardDidHide)
    window.removeEventListener("keyboardWillShow", onKeyboardWillShow);
    window.removeEventListener("keyboardWillHide", onKeyboardWillHide);
}

watch(ionModalIsOpen, value => {
    if (value) {
        registerKeyboardEvents()
    }
    else {
        unregisterKeyboardEvents()
    }
}, { immediate: true })


onMounted(() => {
    determinePresentingElement()
    registerKeyboardEvents()
})

onUnmounted(() => {
    unregisterKeyboardEvents()
})

const onTouchMove = (event) => {
    const scrollTop = event.currentTarget.scrollTop

    if (scrollTop > 0) {
        event.stopPropagation()
    }   
}

</script>

<template>
    <ion-modal
        aria-modal="true"
        class="modal"
        :class="{'fullscreen': fullscreen, 'stacking': stacking && !!presentingElement, 'keep-footer': keepFooterOnKeyboardShow, 'transparent-toolbar': transparentToolbar}"
        :style="{'--keyboard-height': keyboardHeight + 'px'}"
        :is-open="ionModalIsOpen"
        :initial-breakpoint="1"
        :breakpoints="[0, 1]"
        v-on:willPresent="ionModalWillPresent"
        v-on:willDismiss="ionModalWillDismiss"
        v-on:didDismiss="ionModalDidDismiss"
        v-on:didPresent="ionModalDidPresent"
        ref="ionModalRef"
        :presenting-element="presentingElement"
        :data-cy="props.dataCy || (props.title ? ('modal ' + props.title) : undefined)"
    >
        <ion-header :class="{'safe-area-top': fullscreen}">
            <ion-toolbar v-if="toolbar">
                <ion-title v-if="title">
                    {{ title }}
                    <h4 v-if="subtitle" style="margin: 0">{{subtitle}}</h4>
                </ion-title>



                <ion-buttons slot="end">
                    <slot name="toolbar-end" />
                    <ion-button
                        @click="dismiss"
                        class="button-close"
                        shape="round"
                        fill="solid"
                        color="light"
                        data-cy="modal close button"
                    >
                        <ion-icon
                            color="secondary"
                            size="small"
                            src="/icons/x.svg"
                            slot="icon-only"
                        />
                    </ion-button>
                </ion-buttons>
            </ion-toolbar>
        </ion-header>
        <div
            class="body"
            :class="{'no-top-padding': !props.title && !props.transparentToolbar}"
            @touchmove="onTouchMove"
        >
            <slot :dismiss="dismiss" />
        </div>
        <div 
            class="footer"
            v-if="$slots.actions"
        >
            <slot name="actions" :dismiss="dismiss" />
        </div>

    </ion-modal>
</template>

<style lang="scss">
.modal {
    --max-auto-height: 90vh;
    --height: auto;
    align-items: flex-end;

    ion-toolbar {
        --border-width: 0 !important;

        padding-top: calc(var(--custom-spacing-app-content-padding-vertical) / 1.5) !important;
        padding-left: var(--custom-spacing-app-content-padding-horizontal) !important;
        padding-right: calc(var(--custom-spacing-app-content-padding-horizontal) / 2) !important;

        ion-title {
            text-align: left;
            padding-left: 0;
        }

        ion-button {
            margin-left: 0;
            margin-right: 0;
            width: 2rem;
            height: 2rem;
            min-height: 2rem;
            max-height: 2rem;
            max-width: 2rem;
            min-width: 2rem;

            &:not(:last-child) {
                margin-right: 0.75em;
            }
        }
    }

    &.transparent-toolbar {
        ion-header {
            position: absolute;
            pointer-events: none;
        }

        ion-toolbar {
            --ion-toolbar-background: transparent;
        }
    }

    &::part(content) {
        max-height: var(--max-auto-height);
        display: flex;
        flex-direction: column;
    }

    &::part(handle) {
        flex: 0 0 auto;
    }

    > .ion-page {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        overflow-y: hidden;
        overflow-x: hidden;

        > ion-header {
            flex: 0 0 auto;
        }

        > .body {
            width: 100%;
            flex: 1 1 auto;
            overflow-y: auto;
            overflow-x: hidden;
            display: flex;
            flex-direction: column;
            position: relative;
        }

        > .footer {
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            padding-top: 1rem;
            // margin-bottom: env(safe-area-inset-bottom, 0);
            padding-left: var(--custom-spacing-app-content-padding-horizontal);
            padding-right: var(--custom-spacing-app-content-padding-horizontal);
            padding-bottom: max(var(--ion-safe-area-bottom), var(--custom-spacing-app-content-padding-vertical)); // calc(var(--ion-safe-area-bottom) + var(--custom-spacing-app-content-padding-vertical));
            // padding-bottom: 0; // var(--ion-safe-area-bottom);

            > * {
                margin: 0;
                &:not(:first-child) {
                    margin-top: 0.5rem;
                }
            }
        }
    }

    .body {
        padding-top: var(--custom-spacing-app-content-padding-vertical);
        padding-left: var(--custom-spacing-app-content-padding-horizontal);
        padding-right: var(--custom-spacing-app-content-padding-horizontal);
        padding-bottom: var(--custom-spacing-app-content-padding-vertical);
        &.no-top-padding {
            padding-top: 0;
        }
    }

    &.no-padding-left .body {
        padding-left: 0;
    }

    &.no-padding-right .body {
        padding-right: 0;
    }

    &.no-padding-top .body {
        padding-top: 0;
    }

    &.no-padding-bottom .body {
        padding-bottom: 0;
    }

    &.fullscreen {
        --height: 100vh;
       
        &::part(content) {
            max-height: var(--height);
            height: var(--height);
            
            position: absolute;
            top: 0;
        }

        &::part(handle) {
            display: none;
        }

        &.stacking {
            --height: calc(100vh - 3rem);

            &::part(content) {
                top: 3rem;
            }

            ion-header {
                padding-top: 0 !important;
            }
        }
    }

    &.keyboard-visible.show-modal:not(.keep-footer) {
        > .ion-page {
            > .footer,
            ion-footer {
                display: none;
                opacity: 0 !important;
                pointer-events: none !important;
            }
        }
    }
}
</style>
